import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import StatusChip from './StatusChip';
import DispositionChip from './DispositionChip';
import DuplicateBadge from './DuplicateBadge';
import QualityChecksSummary from './QualityChecksSummary';
import ExpandableSection from './ExpandableSection';
import BalanceComparisonTable from './BalanceComparisonTable';
import AccountStatementSummary from './AccountStatementSummary';
import ViewStatementDialog from './dialog-view-statement-pdf';
import { getTransactionDetail } from '../../api/statementqualityreport';
import {
  formatAccountDisplayName,
  formatDate,
  formatDocumentType,
} from '../../utils/scanReportUtils';

/**
 * ExtractedDocumentPanel renders the full detail view for one extracted document
 * (e.g. a single bank statement split from the uploaded PDF).
 *
 * It includes: header with status/disposition, document details, and
 * accordion sections for each pipeline stage (summary scan, deep scan,
 * comparison, final disposition).
 *
 * @param {Object} props
 * @param {number} props.docNumber - 1-based document number
 * @param {Object} props.report - The full report object for this extracted document
 *   from extracted_document_quality_reports[N]
 * @param {boolean} [props.showDetailedChecks] - Whether to show expandable quality check lists (default false)
 */
const ExtractedDocumentPanel = ({ docNumber, report, showDetailedChecks = false }) => {
  const {
    extracted_document_details: details,
    statement_summary: statementSummary,
    comparison_to_summary: comparison,
    post_process_result: postProcess,
  } = report || {};

  const [pdfViewOpen, setPdfViewOpen] = useState(false);
  const [pdfDocumentToView, setPdfDocumentToView] = useState(null);
  const [pdfHighlightedTxn, setPdfHighlightedTxn] = useState(null);
  const [txnLoading, setTxnLoading] = useState(null); // transaction_id currently loading

  const handleTransactionClick = async (txn) => {
    if (!txn.transaction_id) return;
    setTxnLoading(txn.transaction_id);
    try {
      const detail = await getTransactionDetail(txn.transaction_id);
      if (detail && !detail.error) {
        setPdfDocumentToView({
          bucketName: detail.bucket_name,
          objectKey: detail.object_key,
          documentName: detail.document_name,
        });
        setPdfHighlightedTxn({
          bbox: detail.bbox,
          page_number: detail.page_number,
          description: detail.description,
          amount: detail.amount,
          posting_date: detail.posting_date,
        });
        setPdfViewOpen(true);
      } else {
        console.warn('Transaction not found — may need rescan:', detail?.message);
      }
    } catch (err) {
      console.error('Failed to load transaction detail:', err);
    } finally {
      setTxnLoading(null);
    }
  };

  if (!details) return null;

  const isDuplicate = details.status === 'DUPLICATE';
  const isUnsupported = details.status === 'UNSUPPORTED_DOCUMENT_TYPE';
  const disposition = postProcess?.disposition;
  const hasInformationalWarnings = postProcess?.transactions_outside_period?.length > 0
    || (postProcess?.informational_checks
        && Object.values(postProcess.informational_checks).some((v) => v === false));
  const primaryAccount = statementSummary?.accounts?.[0];

  // Use the new title field as the primary display name, with fallbacks
  const documentTitle = details.title
    || (primaryAccount ? formatAccountDisplayName(primaryAccount) : `Document ${docNumber}`);

  // Build summary accounts list (may have multiple accounts in one statement)
  const summaryAccounts = statementSummary?.accounts || [];
  const planScanAccounts = comparison?.account_comparison?.matched_accounts?.map(
    (m) => m.plan_scan_account
  ) || [];

  return (
    <Card variant="outlined" sx={{ position: 'relative' }}>
      <CardContent>
        {/* ─── Panel Header ─── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {documentTitle}
              </Typography>
              {summaryAccounts.length > 1 && (
                <Chip
                  label={`+${summaryAccounts.length - 1} more account${summaryAccounts.length > 2 ? 's' : ''}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Metadata line: doc type · pages · period/date */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {[
                formatDocumentType(details.document_type),
                details.start_page != null && details.end_page != null
                  ? `Pages ${details.start_page}–${details.end_page}`
                  : null,
                details.date_or_period || details.statement_period || null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>

            {/* Institution / organization */}
            {(details.institution_or_org || details.institution) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {details.institution_or_org || details.institution}
              </Typography>
            )}

            {details.additional_notes && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                {details.additional_notes}
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'center' }}>
            {disposition && <DispositionChip disposition={disposition} />}
            {hasInformationalWarnings && (
              <Chip
                label="Has warnings"
                size="small"
                color="warning"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )}
            <StatusChip status={details.status} label={details.displayStatus || undefined} />
            <DuplicateBadge duplicateReason={details.duplicateReason} />
          </Stack>
        </Box>

        {/* Duplicate notice */}
        {isDuplicate && (
          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            This statement was identified as a duplicate and was not re-published.
            The scan results below reflect the processing that was completed before
            the duplicate was detected.
          </Alert>
        )}

        {/* Unsupported document type notice */}
        {isUnsupported && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
            This document type is not currently supported for automated processing.
            No quality checks were performed.
          </Alert>
        )}

        {/* Pipeline sections — hidden for unsupported document types */}
        {!isUnsupported && (
          <>
            <Divider sx={{ mb: 1.5 }} />

        {/* ─── Section 1: Summary Scan ─── */}
        <ExpandableSection
          title="Summary Scan"
          summary={
            statementSummary?.quality_report ? (
              <QualityChecksSummaryInline report={statementSummary.quality_report} showDetailedChecks={showDetailedChecks} />
            ) : null
          }
        >
          <Stack spacing={2}>
            {/* Account & balance overview */}
            <AccountStatementSummary accounts={summaryAccounts} />

            {/* Quality checks */}
            {showDetailedChecks && statementSummary?.quality_report && (
              <QualityChecksSummary
                qualityReport={statementSummary.quality_report}
                title="Summary Quality Checks"
              />
            )}
          </Stack>
        </ExpandableSection>

        {/* ─── Section 2: Deep Scan ─── */}
        {postProcess?.plan_scan_quality_report && (
          <ExpandableSection
            title="Deep Scan"
            showDivider
            summary={
              <QualityChecksSummaryInline report={postProcess.plan_scan_quality_report} showDetailedChecks={showDetailedChecks} />
            }
          >
            <Stack spacing={2}>
              {/* If plan scan found accounts, show them */}
              {planScanAccounts.length > 0 && (
                <AccountStatementSummary accounts={planScanAccounts} />
              )}

              {showDetailedChecks && (
                <QualityChecksSummary
                  qualityReport={postProcess.plan_scan_quality_report}
                  title="Deep Scan Quality Checks"
                />
              )}
            </Stack>
          </ExpandableSection>
        )}

        {/* ─── Section 3: Scan Comparison ─── */}
        {comparison && (
          <ExpandableSection
            title="Scan Comparison"
            showDivider
            summary={<ComparisonSummaryInline comparison={comparison} />}
          >
            <Stack spacing={2}>
              {/* Account matching summary */}
              <AccountMatchSummary accountComparison={comparison.account_comparison} />

              {/* Balance comparison table */}
              <BalanceComparisonTable
                byAccount={comparison.statement_comparison?.by_account}
                accounts={summaryAccounts}
              />
            </Stack>
          </ExpandableSection>
        )}

        {/* ─── Section 4: Final Disposition ─── */}
        {postProcess && (
          <ExpandableSection
            title="Final Disposition"
            showDivider
            defaultExpanded={postProcess.disposition !== 'ACCEPTED' || hasInformationalWarnings}
            summary={
              <Stack direction="row" spacing={0.5} alignItems="center">
                {disposition && <DispositionChip disposition={disposition} />}
                {hasInformationalWarnings && (
                  <Chip
                    label={`${postProcess.transactions_outside_period?.length || 0} warning(s)`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>
            }
          >
            <Stack spacing={1.5}>
              {/* Critical checks */}
              {postProcess.critical_checks && (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Critical Checks
                  </Typography>
                  {Object.entries(postProcess.critical_checks).map(([key, value]) => (
                    <CriticalCheckRow key={key} checkName={key} passed={value} />
                  ))}
                </Box>
              )}

              {/* Comparison metrics */}
              {postProcess.comparison_metrics && (
                <ComparisonMetrics metrics={postProcess.comparison_metrics} />
              )}

              {/* Reasons for review (if any) */}
              {postProcess.reasons_for_review?.length > 0 && (
                <Alert severity="warning">
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    Review Reasons:
                  </Typography>
                  {postProcess.reasons_for_review.map((reason, idx) => (
                    <Typography key={idx} variant="body2">
                      • {typeof reason === 'string' ? reason : JSON.stringify(reason)}
                    </Typography>
                  ))}
                </Alert>
              )}

              {/* Informational warnings (soft checks that don't block acceptance) */}
              {postProcess.informational_checks && (
                <Box>
                  <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                    Informational Checks
                  </Typography>
                  {Object.entries(postProcess.informational_checks).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                      <Typography variant="body2" color={value ? 'success.main' : 'warning.main'}>
                        {value ? '\u2713' : '\u26A0'}
                      </Typography>
                      <Typography variant="body2">
                        {key.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase())}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Out-of-bounds transactions detail */}
              {postProcess.transactions_outside_period?.length > 0 && (
                <Alert severity="info" variant="outlined" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    {postProcess.transactions_outside_period.length} transaction(s) outside statement period
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                          <th style={{ textAlign: 'left', padding: '4px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>Date</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600 }}>Description</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>Amount</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>Offset</th>
                        </tr>
                      </thead>
                      <tbody>
                        {postProcess.transactions_outside_period.slice(0, 20).map((txn, idx) => (
                          <tr
                            key={idx}
                            onClick={() => txn.transaction_id && handleTransactionClick(txn)}
                            style={{
                              cursor: txn.transaction_id ? 'pointer' : 'default',
                              borderBottom: '1px solid #f5f5f5',
                              opacity: txnLoading === txn.transaction_id ? 0.5 : 1,
                            }}
                            onMouseEnter={(e) => { if (txn.transaction_id) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                          >
                            <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{txn.posting_date}</td>
                            <td style={{ padding: '4px 8px', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: txn.transaction_id ? '#1976d2' : 'inherit', textDecoration: txn.transaction_id ? 'underline' : 'none' }}>
                              {txn.description || '\u2014'}
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {txn.amount != null ? `$${Number(txn.amount).toFixed(2)}` : '\u2014'}
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap', color: '#666' }}>
                              {txn.days_outside > 0 ? '+' : ''}{txn.days_outside}d
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                  {postProcess.transactions_outside_period.length > 20 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      ...and {postProcess.transactions_outside_period.length - 20} more
                    </Typography>
                  )}
                </Alert>
              )}
            </Stack>
          </ExpandableSection>
        )}
          </>
        )}
      </CardContent>

      {/* PDF Viewer for out-of-bounds transaction preview */}
      <ViewStatementDialog
        open={pdfViewOpen}
        documentToView={pdfDocumentToView}
        highlightedTxn={pdfHighlightedTxn}
        title={pdfHighlightedTxn ? `Transaction: ${pdfHighlightedTxn.description || 'View'}` : 'View Transaction'}
        onCancel={() => {
          setPdfViewOpen(false);
          setPdfDocumentToView(null);
          setPdfHighlightedTxn(null);
        }}
      />
    </Card>
  );
};

/* ─── Internal Sub-components ─── */

/** Compact inline quality summary shown in the section header */
const QualityChecksSummaryInline = ({ report, showDetailedChecks = false }) => {
  const { passed_checks = 0, total_checks = 0 } = report?.summary || {};
  const allPassed = passed_checks === total_checks && total_checks > 0;
  const passRate = total_checks > 0 ? Math.round((passed_checks / total_checks) * 100) : 0;

  return (
    <Typography
      variant="caption"
      fontWeight={600}
      color={allPassed ? 'success.main' : 'warning.main'}
    >
      {showDetailedChecks ? `${passed_checks}/${total_checks}` : `${passRate}%`}
    </Typography>
  );
};

/** Compact inline comparison result */
const ComparisonSummaryInline = ({ comparison }) => {
  const matched = comparison?.account_comparison?.matched_count ?? 0;
  const missingAccounts = comparison?.account_comparison?.accounts_only_in_summary_scan?.length ?? 0;
  const extraAccounts = comparison?.account_comparison?.accounts_only_in_plan_scan?.length ?? 0;
  const allGood = missingAccounts === 0 && extraAccounts === 0;

  return (
    <Typography
      variant="caption"
      fontWeight={600}
      color={allGood ? 'success.main' : 'warning.main'}
    >
      {matched} matched{missingAccounts > 0 ? `, ${missingAccounts} missing` : ''}
      {extraAccounts > 0 ? `, ${extraAccounts} extra` : ''}
    </Typography>
  );
};

/** Account matching summary with counts */
const AccountMatchSummary = ({ accountComparison }) => {
  if (!accountComparison) return null;

  const {
    matched_count = 0,
    accounts_only_in_plan_scan = [],
    accounts_only_in_summary_scan = [],
  } = accountComparison;

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Chip
        label={`${matched_count} account${matched_count !== 1 ? 's' : ''} matched`}
        size="small"
        color="success"
        variant="outlined"
      />
      {accounts_only_in_plan_scan.length > 0 && (
        <Chip
          label={`${accounts_only_in_plan_scan.length} extra in deep scan`}
          size="small"
          color="warning"
          variant="outlined"
        />
      )}
      {accounts_only_in_summary_scan.length > 0 && (
        <Chip
          label={`${accounts_only_in_summary_scan.length} missing from deep scan`}
          size="small"
          color="error"
          variant="outlined"
        />
      )}
    </Box>
  );
};

/** Single critical check row */
const CriticalCheckRow = ({ checkName, passed }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
    <Typography variant="body2" color={passed ? 'success.main' : 'error.main'}>
      {passed ? '✓' : '✗'}
    </Typography>
    <Typography variant="body2">
      {checkName.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase())}
    </Typography>
  </Box>
);

/** Comparison metrics summary */
const ComparisonMetrics = ({ metrics }) => {
  if (!metrics) return null;

  const issues = [];
  if (metrics.has_missing_accounts) issues.push(`${metrics.missing_accounts_count} missing account(s)`);
  if (metrics.has_extra_accounts) issues.push(`${metrics.extra_accounts_count} extra account(s)`);
  if (metrics.has_missing_statements) issues.push(`${metrics.missing_statements_count} missing statement(s)`);
  if (metrics.has_extra_statements) issues.push(`${metrics.extra_statements_count} extra statement(s)`);

  if (issues.length === 0) {
    return (
      <Typography variant="body2" color="success.main">
        All accounts and statements matched between scans.
      </Typography>
    );
  }

  return (
    <Alert severity="warning" variant="outlined">
      {issues.join(', ')}
    </Alert>
  );
};

/* ─── PropTypes ─── */

QualityChecksSummaryInline.propTypes = {
  report: PropTypes.shape({
    summary: PropTypes.shape({
      passed_checks: PropTypes.number,
      total_checks: PropTypes.number,
    }),
  }),
  showDetailedChecks: PropTypes.bool,
};

ComparisonSummaryInline.propTypes = {
  comparison: PropTypes.object,
};

AccountMatchSummary.propTypes = {
  accountComparison: PropTypes.object,
};

CriticalCheckRow.propTypes = {
  checkName: PropTypes.string.isRequired,
  passed: PropTypes.bool.isRequired,
};

ComparisonMetrics.propTypes = {
  metrics: PropTypes.object,
};

ExtractedDocumentPanel.propTypes = {
  docNumber: PropTypes.number.isRequired,
  report: PropTypes.shape({
    extracted_document_details: PropTypes.object,
    statement_summary: PropTypes.object,
    comparison_to_summary: PropTypes.object,
    post_process_result: PropTypes.object,
  }).isRequired,
  showDetailedChecks: PropTypes.bool,
};

export default ExtractedDocumentPanel;